import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { Card, CardContent } from '../../../shared/components/ui/card';

const PERSONALIZATION_OPTIONS = {
  expression: {
    label: 'Expression',
    options: [
      { value: '', label: 'Default (Neutral)' },
      { value: 'neutral', label: 'Neutral & Confident' },
      { value: 'soft_smile', label: 'Soft Smile' },
      { value: 'friendly_smile', label: 'Friendly Smile' },
      { value: 'confident_smile', label: 'Confident Smile' }
    ]
  },
  presence: {
    label: 'Presence',
    options: [
      { value: '', label: 'Default' },
      { value: 'calm', label: 'Calm Presence' },
      { value: 'approachable', label: 'Approachable Presence' },
      { value: 'assertive', label: 'Assertive Presence' }
    ]
  },
  attire: {
    label: 'Attire',
    options: [
      { value: '', label: 'Default (Professional)' },
      { value: 'formal', label: 'Formal' },
      { value: 'business_casual', label: 'Business Casual' },
      { value: 'smart_casual', label: 'Smart Casual' },
      { value: 'creative_professional', label: 'Creative Professional' }
    ]
  },
  professional_context: {
    label: 'Industry Context',
    options: [
      { value: '', label: 'Default (Universal)' },
      { value: 'corporate', label: 'Corporate / Enterprise' },
      { value: 'startup_tech', label: 'Startup / Tech' },
      { value: 'consultant', label: 'Consultant / Coach' },
      { value: 'creative', label: 'Creative Professional' },
      { value: 'finance_legal', label: 'Finance / Legal' }
    ]
  },
  background: {
    label: 'Background',
    options: [
      { value: '', label: 'Default (Clean Neutral)' },
      { value: 'clean_studio_light', label: 'Clean Studio (Light)' },
      { value: 'clean_studio_dark', label: 'Clean Studio (Dark)' },
      { value: 'office_blurred', label: 'Office-Style Blurred' },
      { value: 'minimal_gradient', label: 'Minimal Gradient' }
    ]
  },
  lighting: {
    label: 'Lighting',
    options: [
      { value: '', label: 'Default (Soft Natural)' },
      { value: 'soft_natural', label: 'Soft Natural Lighting' },
      { value: 'professional_studio', label: 'Professional Studio Lighting' },
      { value: 'high_contrast', label: 'High-Contrast Executive' }
    ]
  },
  finish: {
    label: 'Finish',
    options: [
      { value: '', label: 'Default (Natural)' },
      { value: 'natural', label: 'Natural Finish' },
      { value: 'light_polish', label: 'Light Polished Finish' },
      { value: 'premium_editorial', label: 'Premium Editorial Finish' }
    ]
  },
  framing: {
    label: 'Framing',
    options: [
      { value: '', label: 'Default (Head & Shoulders)' },
      { value: 'head_shoulders', label: 'Head & Shoulders' },
      { value: 'chest_up', label: 'Chest-Up Portrait' }
    ]
  },
  pose: {
    label: 'Posture',
    options: [
      { value: '', label: 'Default (Straight)' },
      { value: 'straight', label: 'Straight Posture' },
      { value: 'slight_lean', label: 'Slight Natural Lean' }
    ]
  }
};

export const PersonalizationOptions = ({ personalization, onPersonalizationChange, isLoading }) => {
  const handleSelectChange = (category, value) => {
    onPersonalizationChange({
      ...personalization,
      [category]: value || null
    });
  };

  return (
    <div className="mt-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-medium text-foreground">Personalize Your Headshot</p>
            <p className="text-sm text-muted-foreground">Optional - Customize appearance</p>
          </div>
        </div>

        <Card className="border-border/50">
          <CardContent className="pt-6 space-y-4">
            <div className="text-sm text-muted-foreground mb-4">
              Select preferences to customize your professional headshot. Leave as default to use standard professional styling.
            </div>

            {Object.entries(PERSONALIZATION_OPTIONS).map(([category, config]) => (
              <div key={category} className="space-y-2">
                <label htmlFor={category} className="text-sm font-medium text-foreground">
                  {config.label}
                </label>
                <select
                  id={category}
                  value={personalization[category] || ''}
                  onChange={(e) => handleSelectChange(category, e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm
                    hover:border-primary/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20
                    disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {config.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}

          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
